import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Leaf, Package, Award, Users, Globe, TrendingUp } from 'lucide-react';
import { stats, testimonials } from '../utils/mockData';

export const Home = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1503694978374-8a2fa686963a"
            alt="Modern Printing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-lime-900/70"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="flex items-center space-x-2 mb-6 animate-in slide-in-from-left duration-700">
              <Leaf className="w-6 h-6 text-lime-400" />
              <span className="text-lime-400 font-medium">Sürdürülebilir Ambalaj Çözümleri</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-in slide-in-from-left duration-700 delay-100">
              Kalite ve Çevre Dostu{' '}
              <span className="text-lime-400">Üretim</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 animate-in slide-in-from-left duration-700 delay-200">
              1997'den beri sektörde yer alan, 2005 yılı itibariyle İstanbul merkezli faaliyetlere devam eden, %100 kadın girişimci gücüyle kurulmuş, ofset baskılı karton kutu ambalaj sektöründe uluslararası standartlarda hizmet sunuyoruz.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-in slide-in-from-left duration-700 delay-300">
              <Link
                to="/urunler"
                className="group px-8 py-4 bg-lime-600 text-white rounded-lg font-semibold hover:bg-lime-700 transition-all hover:shadow-xl hover:shadow-lime-600/20 flex items-center justify-center space-x-2"
              >
                <span>Ürünlerimiz</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/iletisim"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg font-semibold hover:bg-white/20 transition-all flex items-center justify-center"
              >
                İletişime Geçin
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-lime-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Portfolio / Product Showcase */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {[
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/6i594vsf_1.jpg',
                title: 'Elektrikli Ev Aletleri',
                category: 'Ev Aletleri'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/1hygyklg_2.jpg',
                title: 'Kahve & İçecek Makineleri',
                category: 'Ev Aletleri'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/uc1q6dp6_3.jpg',
                title: 'Kedi Kumu Ambalajları',
                category: 'Pet Products'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/74jxo9i5_4.jpg',
                title: 'Pet Food Kutuları',
                category: 'Pet Products',
                whiteBg: true
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/swabscx9_5.png',
                title: 'Otomotiv Yedek Parça',
                category: 'Otomotiv',
                whiteBg: true
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/6eix7cjg_11.png',
                title: 'Köpek Maması',
                category: 'Pet Products',
                whiteBg: true
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/n8kpbpug_7.jpg',
                title: 'Premium Kedi Kumu',
                category: 'Pet Products'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/hefo3avy_8.jpg',
                title: 'Kedi Kumu Kutular',
                category: 'Pet Products'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/fg4iohmn_9.JPG',
                title: 'Premium Kedi Maması',
                category: 'Pet Products'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/d0d9rz96_10.png',
                title: 'Bio Kedi Maması',
                category: 'Pet Products',
                whiteBg: true
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/rqy5u7lp_a59e5143bd862eed78596d71aa6f3aae.webp',
                title: 'Eğitici Oyuncak Kutuları',
                category: 'Oyuncak',
                whiteBg: true
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/ba2qy2kl_10dd08d0a083264a7dc636b3182db34d.jpg',
                title: 'Oyuncak Robot Ambalajı',
                category: 'Oyuncak',
                whiteBg: true
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/g7lbrxqy_oyuncak3.webp',
                title: 'Müzikli Oyuncak Kutusu',
                category: 'Oyuncak',
                whiteBg: true
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/khrrioax_08f10b8c9c003b447a2a5d39f316c5f1.webp',
                title: 'Bebek Puzzle Seti',
                category: 'Oyuncak',
                whiteBg: true
              }
            ].map((product, index) => (
              <div 
                key={index}
                className="group relative bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-lime-300 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className={`aspect-square p-4 flex items-center justify-center overflow-hidden ${
                  product.whiteBg ? 'bg-white' : 'bg-gray-50'
                }`} style={product.whiteBg ? { 
                  background: 'white',
                  mixBlendMode: 'multiply'
                } : {}}>
                  <img 
                    src={product.url}
                    alt={product.title}
                    className={`w-full h-full object-contain group-hover:scale-110 transition-transform duration-500 ${
                      product.whiteBg ? 'mix-blend-multiply' : ''
                    }`}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/urunler"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-lime-600 text-white rounded-lg font-semibold hover:bg-lime-700 transition-all hover:shadow-lg"
            >
              <span>Tüm Ürünleri İnceleyin</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-lime-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon === 'Award' ? Award : 
                                   stat.icon === 'Users' ? Users :
                                   stat.icon === 'Package' ? Package : Globe;
              return (
                <div key={index} className="text-center group">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-lime-50 rounded-2xl mb-4 group-hover:bg-lime-100 transition-colors">
                    <IconComponent className="w-8 h-8 text-lime-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                  {stat.unit && <div className="text-xs text-gray-500">{stat.unit}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-lime-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Neden <span className="text-lime-600">INTERPACK A.Ş?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Sektördeki deneyimimiz ve modern üretim altyapımızla fark yaratıyoruz
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: CheckCircle,
                title: 'Uluslararası Kalite',
                description: 'ISO 9001 ve FSC sertifikalı üretim süreçleri ile garantili kalite'
              },
              {
                icon: Leaf,
                title: 'Çevre Dostu Üretim',
                description: 'Geri dönüştürülebilir malzemeler ve sürdürülebilir üretim anlayışı'
              },
              {
                icon: TrendingUp,
                title: 'Yüksek Kapasite',
                description: 'Aylık 1 milyon adetten fazla üretim kapasitesi ile hızlı teslimat'
              },
              {
                icon: Award,
                title: 'Deneyimli Kadro',
                description: '20 yıllık sektör tecrübesi ve uzman ekibimiz'
              },
              {
                icon: Globe,
                title: 'Küresel Hizmet',
                description: '15+ ülkeye ihracat deneyimi ve uluslararası referanslar'
              },
              {
                icon: Package,
                title: 'Özel Tasarım',
                description: 'Müşteri ihtiyaçlarına özel tasarım ve baskı çözümleri'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="group bg-white p-8 rounded-2xl hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-lime-200"
              >
                <div className="w-14 h-14 bg-lime-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-lime-600 transition-colors">
                  <feature.icon className="w-7 h-7 text-lime-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Müşterilerimiz Ne Diyor?
            </h2>
            <p className="text-xl text-gray-600">
              Başarı hikayelerimiz müşteri memnuniyetinde gizli
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id}
                className="bg-gradient-to-br from-lime-50 to-white p-8 rounded-2xl border border-lime-100 hover:shadow-lg transition-shadow"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-lime-600">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-lime-600 to-lime-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Projeleriniz İçin Teklif Alın
          </h2>
          <p className="text-xl text-lime-100 mb-8 max-w-2xl mx-auto">
            Uzman ekibimiz, ihtiyaçlarınıza en uygun ambalaj çözümünü sunmak için hazır
          </p>
          <Link
            to="/iletisim"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-lime-600 rounded-lg font-semibold hover:bg-gray-50 transition-all hover:shadow-xl"
          >
            <span>Hemen İletişime Geçin</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};
